import { FC, useEffect, useState } from "react";
import "../../styles/components.css";
import "../../styles/standard-viewer.css";
import { useAppStateContext } from "../../middleware/context/state-context-provider";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  BuildCanvasCrumbs,
  BuildLocationPath,
  CheckAuthentication,
  HandleLogOut,
  SelectCrumbUser,
  UpdateUserLastActionDate,
} from "../../helper/ui/methods";
import ResponseDialog from "../ui/user/response-dialog";
import { StandardViewport } from "../ui/viewer/standard-viewport";
import { PropertiesMenu } from "../ui/viewer/properties-menu";
import { LayersMenu } from "../ui/viewer/layers-menu";
import { ModelTreeMenu } from "../ui/viewer/model-tree-menu";
import { MeasureMenu } from "../ui/viewer/measure-menu";
import { SectionMenu } from "../ui/viewer/section-menu";
import { ViewerToolbar } from "../ui/viewer/viewer-toolbar";
import { StandardHandler } from "../../middleware/handlers/standard-handler";
import type {
  MeasureTool,
  MeasureUnits,
  ViewerNavigation,
  ViewerProjection,
} from "../../core/standard/viewer-tools";
import { FormatDateBE } from "../../helper/general/methods";
import { ModelUploadInterface } from "../../middleware/interfaces/file/model-upload-interface";
import MenuButton from "../ui/user/menu-button";
import MainNavigationPanel from "../ui/user/main-navigation-panel";
import MainFooterPanel from "../ui/user/main-footer-panel";
import CanvasHeader from "../ui/user/canvas-header";
import * as BUI from "@thatopen/ui";

// Add this before the StandardViewer component
declare module "react" {
  namespace JSX {
    interface IntrinsicElements {
      "bim-panel": any;
      "bim-panel-section": any;
    }
  }
}

type ViewerPanel =
  | "LAYERS"
  | "MODEL_TREE"
  | "PROPERTIES"
  | "MEASURE"
  | "SECTION";

export const StandardViewer: FC = () => {
  const [state, dispatch] = useAppStateContext();
  const navigate = useNavigate();
  const location = useLocation();
  // one panel docks at a time, so the model keeps the rest of the canvas
  const [openPanel, setOpenPanel] = useState<ViewerPanel | null>(null);
  const [measureTool, setMeasureTool] = useState<MeasureTool>("LENGTH");
  const [measureUnits, setMeasureUnits] = useState<MeasureUnits>("IMPERIAL");
  const [projection, setProjection] = useState<ViewerProjection>("Perspective");
  const [navigation, setNavigation] = useState<ViewerNavigation>("Orbit");

  const TogglePanel = (panel: ViewerPanel) => {
    setOpenPanel(openPanel === panel ? null : panel);
  };

  // The open panel decides what a double-click on the model does: measuring
  // and sectioning own the canvas while their panel is open, and closing it
  // hands the canvas back to selection.
  useEffect(() => {
    StandardHandler.SetActiveTool(
      openPanel === "MEASURE"
        ? measureTool
        : openPanel === "SECTION"
          ? "SECTION"
          : "SELECT",
    );
  }, [openPanel, measureTool]);

  useEffect(() => {
    StandardHandler.SetMeasureUnits(measureUnits);
  }, [measureUnits]);

  // The viewer sits outside the address-driven pages, so the folder it was
  // opened from is the entry before it. Arriving any other way (a link, a new
  // tab) there is no such entry, and the open folder's address stands in.
  const HandleViewerReturn = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(BuildLocationPath(state.user!), { replace: true });
    }
  };

  // Moving the address is enough: LocationHandler opens what it points at.
  const HandleViewerCrumbSelect = (crumbIndex: number) => {
    navigate(BuildLocationPath(SelectCrumbUser(state.user!, crumbIndex)));
  };

  useEffect(() => {
    if (state.user === null || state.user === undefined) {
      console.log("CHECKING AUTHENTICATION!");
      CheckAuthentication(dispatch);
    } else {
      BUI.Manager.init();
    }
  }, []);

  useEffect(() => {
    if (state.user) {
      UpdateUserLastActionDate(state.user, dispatch);
    }
  }, [state.user]);

  useEffect(() => {
    //PREPARE AND UPLOAD MODEL FRAGMENTS
    if (state.modelConversion) {
      const uploadModel: ModelUploadInterface = {
        email: state.user?.email!,
        projectID: state.user?.openProject?.projectID!,
        projectUserRole: state.user?.openProject?.projectUserRole!,
        modelFileParentID: state.user?.openFolder
          ? state.user?.openFolder.folderID
          : null,
        modelName: state.modelConversion["modelName"],
        modelFileType: "ifc",
        modelFileSize: state.openModel["modelFileSize"],
        modelFragmentsSize: state.modelConversion["modelFragmentsSize"],
        modelUploadDate: FormatDateBE(new Date()),
        modelData: {
          modelFileData: state.openModel["modelFileData"],
          modelFragmentsData: state.modelConversion["modelFragmentsData"],
        },
        type: "ModelUploadInterface",
      };

      dispatch({ type: "UPLOAD_FILE", payload: uploadModel });
      // Cleared so re-opening the same model converts and uploads again rather
      // than being swallowed by an unchanged dependency.
      dispatch({ type: "CONVERT_MODEL", payload: null });
    }
  }, [state.modelConversion]);

  return (
    <>
      {(() => {
        if (state.user !== null && state.user !== undefined) {
          return (
            <div
              id="pageWrapper"
              onClick={() => UpdateUserLastActionDate(state.user!, dispatch)}
            >
              <MainNavigationPanel user={state.user} />
              <div id="contentWrapper">
                <div id="leftMenu">
                  <div className="menuButtonWrapper">
                    <MenuButton
                      imageSource="assets/icons/layers.png"
                      alternativeText="LAYERS"
                      dispatch={() => {
                        TogglePanel("LAYERS");
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/model-tree.png"
                      alternativeText="MODEL TREE"
                      dispatch={() => {
                        TogglePanel("MODEL_TREE");
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/properties.png"
                      alternativeText="PROPERTIES"
                      dispatch={() => {
                        TogglePanel("PROPERTIES");
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/measure.png"
                      alternativeText="MEASURE"
                      dispatch={() => {
                        TogglePanel("MEASURE");
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/section.png"
                      alternativeText="SECTION"
                      dispatch={() => {
                        TogglePanel("SECTION");
                      }}
                    />
                    <MenuButton
                      imageSource="assets/icons/return.png"
                      alternativeText="RETURN"
                      dispatch={HandleViewerReturn}
                    />
                    <MenuButton
                      imageSource="assets/icons/logout.png"
                      alternativeText="LOG OUT"
                      dispatch={() => {
                        HandleLogOut(dispatch);
                      }}
                    />
                  </div>
                </div>
                <div id="content" className="canvasSurface">
                  <CanvasHeader
                    crumbs={
                      state.openModel?.["modelName"]
                        ? [
                            ...BuildCanvasCrumbs(state.user),
                            state.openModel["modelName"],
                          ]
                        : BuildCanvasCrumbs(state.user)
                    }
                    onCrumbSelect={HandleViewerCrumbSelect}
                  />
                  <StandardViewport />
                  <ViewerToolbar
                    projection={projection}
                    navigation={navigation}
                    setProjection={setProjection}
                    setNavigation={setNavigation}
                    dispatch={dispatch}
                  />
                  <ResponseDialog
                    user={state.user}
                    title="Server Response"
                    message={state.response}
                    progress={state.uploadProgress}
                    archiveProgress={state.archiveProgress}
                    dispatch={dispatch}
                  />
                </div>
                <div id="rightMenu">
                  <LayersMenu layersPanelReveal={openPanel === "LAYERS"} />
                  <ModelTreeMenu
                    modelTreePanelReveal={openPanel === "MODEL_TREE"}
                  />
                  <PropertiesMenu
                    propertiesPanelReveal={openPanel === "PROPERTIES"}
                  />
                  <MeasureMenu
                    measurePanelReveal={openPanel === "MEASURE"}
                    measureTool={measureTool}
                    setMeasureTool={setMeasureTool}
                    measureUnits={measureUnits}
                    setMeasureUnits={setMeasureUnits}
                  />
                  <SectionMenu sectionPanelReveal={openPanel === "SECTION"} />
                </div>
              </div>
              <MainFooterPanel />
            </div>
          );
        } else {
          if (state.response !== "") {
            return (
              <div
                id="content"
                onClick={() => UpdateUserLastActionDate(state.user!, dispatch)}
              >
                <ResponseDialog
                  user={state.user!}
                  title="Server Response"
                  message={state.response}
                  progress={state.uploadProgress}
                  archiveProgress={state.archiveProgress}
                  dispatch={dispatch}
                />
              </div>
            );
          } else {
            return <Navigate to={"/authentication&registration"} />;
          }
        }
      })()}
    </>
  );
};
